import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { ValueFieldProps } from '@sitecore-jss/sitecore-jss-react-forms';
import classNames from 'classnames';

import { icon_close_medium } from '@gn-audio/icons';
import { JabraIcon } from '@gn-audio/jss-components/components/JabraIcon/JabraIcon';

import { FileUploadViewModel } from 'src/types/fileUpload';

import TspLabel from './TspLabel';
import { clearStoredFiles, getStoredFiles, storeFiles } from './tspNavigationState';

const TspFileUpload: React.FC<ValueFieldProps<FileUploadViewModel>> = (props) => {
  const { errors, field, ...restProps } = props;
  const FieldValidationErrors = props.fieldValidationErrorsComponent;

  const { maxFileCount, allowedContentTypes, placeholderFilesSelected } = field.model;
  const hasAnyError = !!errors?.length;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);

  const [fileErrors, setFileErrors] = useState<string[]>([]);

  const getFileSizeUnitLabel = (unit: number): string => {
    switch (unit) {
      case 1:
        return 'Bytes';
      case 1024:
        return 'KB';
      case 1024 * 1024:
        return 'MB';
      case 1024 * 1024 * 1024:
        return 'GB';
      default:
        return 'Bytes';
    }
  };

  const validateFile = useCallback(
    (file: File): string[] => {
      const errors: string[] = [];
      if (allowedContentTypes && !allowedContentTypes.split(',').includes(file.type)) {
        errors.push(`Invalid file type`);
      }
      const maxFileSize = field.model.maxFileSize * field.model.fileSizeUnit;
      if (file.size > maxFileSize) {
        const unitLabel = getFileSizeUnitLabel(field.model.fileSizeUnit);
        errors.push(`File size exceeds the limit of ${field.model.maxFileSize} ${unitLabel}`);
      }
      return errors;
    },
    [allowedContentTypes, field.model.maxFileSize, field.model.fileSizeUnit]
  );

  useEffect(() => {
    const stored = getStoredFiles(field.valueField.name);
    if (stored) {
      setSelectedFiles(stored);
      const fileArray = [...stored];
      props.onChange(field.valueField.name, fileArray, restProps.isValid, []);
    }
  }, [field.valueField.name, props, restProps.isValid]);

  const { title } = field.model;

  const onChange = (files: FileList | null) => {
    const fileArray = files ? [...files] : [];
    props.onChange(field.valueField.name, fileArray, restProps.isValid, []);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    setFileErrors([]);

    if (files && files.length > 0) {
      const file = files[0];
      const validationErrors = validateFile(file);
      setFileErrors(validationErrors);

      if (validationErrors.length > 0) {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setSelectedFiles(null);
        clearStoredFiles(field.valueField.name);
        onChange(null);
        return;
      }
    }

    setSelectedFiles(files);
    storeFiles(field.valueField.name, files);
    onChange(files);
  };

  const clearFiles = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      setSelectedFiles(null);
      clearStoredFiles(field.valueField.name);
      onChange(null);
    }
  };

  const handleAreaClick = () => {
    fileInputRef.current?.click();
  };

  const getDisplayText = () => {
    if (selectedFiles && selectedFiles.length > 0) {
      if (selectedFiles.length === 1) {
        return selectedFiles[0].name;
      }
      return `${selectedFiles.length} ${placeholderFilesSelected || 'files selected'}`;
    }
    return null;
  };

  /**
   * Convert MIME types to user-friendly file extensions.
   * e.g., "application/pdf,image/jpeg,image/png" -> "pdf/jpg/png"
   */
  const getFileTypeHints = () => {
    if (!allowedContentTypes) return '';

    const mimeToExt: Record<string, string> = {
      'application/pdf': 'pdf',
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'image/webp': 'webp',
      'application/msword': 'doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      'application/vnd.ms-excel': 'xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
      'text/plain': 'txt',
      'text/csv': 'csv',
    };

    const types = allowedContentTypes.split(',').map((type) => type.trim());
    const extensions = types
      .map((type) => mimeToExt[type] || type.replace(/^.*\//, ''))
      .filter((ext, index, arr) => arr.indexOf(ext) === index);

    return extensions.join('/');
  };

  const displayText = getDisplayText();
  const fileTypeHints = getFileTypeHints();

  return (
    <div className="tsp-file-upload">
      <TspLabel {...props} />

      <input
        ref={fileInputRef}
        className="sr-only"
        type="file"
        id={field.valueField.id}
        name={field.valueField.name}
        accept={allowedContentTypes || '.jpg,.pdf'}
        multiple={maxFileCount > 1}
        required={field.model.required}
        onChange={handleFileChange}
      />

      <div
        className={classNames('tsp-file-upload-area', {
          'tsp-file-upload-area--error': hasAnyError || fileErrors.length > 0,
        })}
        onClick={handleAreaClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleAreaClick();
          }
        }}
      >
        {displayText && (
          <>
            <span className="tsp-file-upload-filename">{displayText}</span>
            <button
              type="button"
              className="tsp-file-upload-clear"
              onClick={clearFiles}
              aria-label={title}
            >
              <JabraIcon Icon={icon_close_medium} />
            </button>
          </>
        )}
      </div>

      {fileTypeHints && <div className="tsp-file-upload-hints">{fileTypeHints}</div>}

      {!!FieldValidationErrors && fileErrors.length > 0 && (
        <FieldValidationErrors field={field} errors={fileErrors} {...restProps} />
      )}
    </div>
  );
};

export default TspFileUpload;
